import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { subsidiaries } from '../../data/mock';
import { useLanguage } from '../../context/LanguageContext';
import SubsidiaryIcon from '../ui/SubsidiaryIcon';

/**
 * EcosystemDiagram — interactive orbit
 * The 8 subsidiary logos slowly orbit around the static SWG hub.
 * Each logo counter-rotates to stay upright. Hover a logo to pause the orbit
 * and emphasise it. SWG hub gently pulses.
 *
 * Centering note (Framer Motion vs Tailwind):
 *  motion.div applies its own `transform`, which OVERRIDES Tailwind
 *  `translate-x-1/2 translate-y-1/2`. We therefore use STATIC wrappers for
 *  positioning (transform: translate(-50%, -50%)) and let motion handle
 *  rotate / scale animations only.
 */
const RING_R = 34;
const INNER_R = 18;
const ORBIT_SECONDS = 50;

const EcosystemDiagram = () => {
  const { language } = useLanguage();
  const count = subsidiaries.length; // 8

  const positionFor = (index) => {
    const angle = (index / count) * Math.PI * 2 - Math.PI / 2;
    const x = 50 + RING_R * Math.cos(angle);
    const y = 50 + RING_R * Math.sin(angle);
    return { x, y };
  };

  return (
    <div
      className="group relative w-full aspect-square max-w-[560px] mx-auto"
      data-testid="ecosystem-diagram"
    >
      {/* SVG decoration layer (static) */}
      <svg
        viewBox="0 0 100 100"
        className="absolute inset-0 w-full h-full"
        aria-hidden="true"
      >
        <defs>
          <radialGradient id="hubGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="rgba(91,122,138,0.5)" />
            <stop offset="55%" stopColor="rgba(91,122,138,0.08)" />
            <stop offset="100%" stopColor="transparent" />
          </radialGradient>
        </defs>
        <circle cx="50" cy="50" r="42" fill="url(#hubGlow)" />
        <circle
          cx="50"
          cy="50"
          r={RING_R}
          fill="none"
          stroke="rgba(91,122,138,0.3)"
          strokeWidth="0.25"
          strokeDasharray="0.8 0.8"
        />
        <circle
          cx="50"
          cy="50"
          r={INNER_R}
          fill="none"
          stroke="rgba(91,122,138,0.2)"
          strokeWidth="0.18"
        />
      </svg>

      {/* Rotating orbit group — CSS animation so it can be paused on hover */}
      <div
        className="absolute inset-0 group-hover:[animation-play-state:paused]"
        style={{
          animation: `spin ${ORBIT_SECONDS}s linear infinite`,
          transformOrigin: '50% 50%',
        }}
      >
        {subsidiaries.map((sub, i) => {
          const { x, y } = positionFor(i);
          return (
            <div
              key={sub.id}
              className="absolute z-20"
              style={{
                left: `${x}%`,
                top: `${y}%`,
                transform: 'translate(-50%, -50%)',
                width: '92px',
              }}
            >
              {/* Counter-rotation keeps icon + label upright */}
              <div
                className="group-hover:[animation-play-state:paused]"
                style={{
                  animation: `spin ${ORBIT_SECONDS}s linear infinite reverse`,
                  transformOrigin: '50% 50%',
                }}
              >
                <Link
                  to={`/subsidiary/${sub.id}`}
                  className="block"
                  data-testid={`ecosystem-node-${sub.id}`}
                >
                  <motion.div
                    initial="rest"
                    whileHover="hover"
                    animate="rest"
                    variants={{
                      rest: { scale: 1, y: 0 },
                      hover: { scale: 1.2, y: -4 },
                    }}
                    transition={{ type: 'spring', stiffness: 280, damping: 18 }}
                    className="relative flex flex-col items-center"
                  >
                    <SubsidiaryIcon subsidiary={sub} size="md" />
                    <div
                      className="mt-2 text-center text-[10px] md:text-[11px] font-semibold tracking-wide leading-tight"
                      style={{ color: sub.color }}
                    >
                      {sub.name}
                    </div>

                    {/* Tagline tooltip — appears on hover above the icon */}
                    <motion.div
                      variants={{
                        rest: { opacity: 0, y: 4, pointerEvents: 'none' },
                        hover: { opacity: 1, y: 0, pointerEvents: 'auto' },
                      }}
                      transition={{ duration: 0.18, ease: 'easeOut' }}
                      className="absolute bottom-full mb-3 left-1/2 -translate-x-1/2 w-[180px] z-30"
                      data-testid={`ecosystem-tooltip-${sub.id}`}
                    >
                      <div
                        className="px-3 py-2 rounded-lg text-[11px] text-white text-center leading-snug shadow-[0_10px_30px_rgba(0,0,0,0.5)] backdrop-blur-sm border"
                        style={{
                          background: 'rgba(14,17,18,0.92)',
                          borderColor: `${sub.color}80`,
                        }}
                      >
                        {sub.i18n[language].tagline}
                      </div>
                      <div
                        className="absolute left-1/2 -translate-x-1/2 top-full w-2 h-2 rotate-45 border-r border-b"
                        style={{
                          background: 'rgba(14,17,18,0.92)',
                          borderColor: `${sub.color}80`,
                          marginTop: '-4px',
                        }}
                      />
                    </motion.div>
                  </motion.div>
                </Link>
              </div>
            </div>
          );
        })}
      </div>

      {/* SWG hub — static centered, with subtle breathing pulse */}
      <div
        className="absolute top-1/2 left-1/2 z-10"
        style={{ transform: 'translate(-50%, -50%)' }}
        data-testid="ecosystem-hub"
      >
        <motion.div
          initial={{ scale: 0.85, opacity: 0 }}
          whileInView={{ scale: 1, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: [0.25, 1, 0.5, 1] }}
          className="relative flex flex-col items-center"
        >
          {/* Pulsing glow ring behind the badge */}
          <motion.span
            className="absolute inset-0 rounded-full pointer-events-none"
            style={{ boxShadow: '0 0 0 0 rgba(91,122,138,0.5)' }}
            animate={{ boxShadow: [
              '0 0 0 0 rgba(91,122,138,0.45)',
              '0 0 0 22px rgba(91,122,138,0)',
              '0 0 0 0 rgba(91,122,138,0)',
            ] }}
            transition={{ duration: 3.2, ease: 'easeOut', repeat: Infinity }}
            aria-hidden="true"
          />
          <div
            className="w-24 h-24 md:w-28 md:h-28 rounded-full flex items-center justify-center shadow-[0_10px_40px_rgba(91,122,138,0.5)] border border-[rgba(255,255,255,0.1)]"
            style={{ background: 'radial-gradient(circle at 30% 30%, #6e8d9c 0%, #3d5a6a 75%)' }}
            aria-label="Smartworld Group"
          >
            <img
              src="/logos/swg-icon.png"
              alt="Smartworld Group"
              className="w-14 h-14 md:w-16 md:h-16 object-contain"
            />
          </div>
          <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 text-white font-black text-xs md:text-sm tracking-[0.25em] whitespace-nowrap">
            SWG
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default EcosystemDiagram;
